import type { AuthTokens } from "../types";
import { tokenStorage } from "../storage";


/**
 * Exchanges Okta session token for OAuth tokens (access, id, refresh)
 * 
 * IMPORTANT: In browser environment with proxy authentication, we cannot use
 * oktaAuth.token.getWithoutPrompt() as it makes CORS requests to Okta.
 * Instead, we store the sessionToken and treat it as our access token.
 * 
 * @param sessionToken Okta session token
 * @param userData Optional user data from Okta response
 * @returns AuthTokens object with sessionToken as accessToken
 */
export async function exchangeSessionTokenForOAuthTokens(
  sessionToken: string,
  userData?: { id: string; profile: { login: string; firstName: string; lastName: string } }
): Promise<AuthTokens> {
  try {
    console.log("🔑 [exchangeSessionToken] Starting with sessionToken:", {
      sessionTokenLength: sessionToken?.length,
      sessionTokenPreview: sessionToken?.substring(0, 20) + '...',
      hasUserData: !!userData,
    });
    
    // In proxy mode (browser), we cannot make CORS requests to Okta's authorize endpoint
    // Instead, we'll use the sessionToken directly as our auth token
    // The sessionToken is sufficient for authenticated API calls when using proxy
    
    // Store the raw sessionToken separately for SSO redirects
    console.log("🔑 [exchangeSessionToken] Storing okta_session_token in sessionStorage...");
    sessionStorage.setItem("okta_session_token", sessionToken);
    console.log("🔑 [exchangeSessionToken] okta_session_token stored successfully");
    
    const authTokens: AuthTokens = {
      accessToken: sessionToken, // Use sessionToken as access token in proxy mode
      idToken: sessionToken, // Also use as idToken for now
      refreshToken: undefined,
      expiresAt: Math.floor(Date.now() / 1000) + 3600, // 1 hour expiry
      tokenType: "Bearer",
    };

    console.log("🔑 [exchangeSessionToken] Storing tokens via tokenStorage...");
    await tokenStorage.setTokens(authTokens);
    console.log("🔑 [exchangeSessionToken] Tokens stored via tokenStorage");
    
    // Store user data in sessionStorage if provided
    if (userData) {
      const userInfo = {
        sub: userData.id,
        email: userData.profile.login,
        givenName: userData.profile.firstName,
        familyName: userData.profile.lastName,
        name: `${userData.profile.firstName} ${userData.profile.lastName}`,
      };
      sessionStorage.setItem("okta_user_info", JSON.stringify(userInfo));
      console.log("🔑 [exchangeSessionToken] User info stored");
    }
    
    console.log("🔑 [exchangeSessionToken] Complete - returning tokens");
    return authTokens;
  } catch (error: any) {
    // Enhanced error message with details
    const errorMessage = error?.message ?? "Unknown error during token exchange";
    const errorDetails = error?.errorCode 
      ? ` (Error code: ${error.errorCode})` 
      : "";
    
    throw new Error(`Failed to exchange session token for OAuth tokens: ${errorMessage}${errorDetails}`);
  }
}
